import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useAuth, AuthTokens } from "../hooks/useAuth";
import { User } from "../types";

// Role hierarchy (higher roles inherit permissions from lower roles)
export const ROLE_HIERARCHY = {
  intern: 0,
  employee: 1,
  team_leader: 2,
  project_manager: 3,
  manager: 3,
  hr_manager: 3,
  admin: 4,
  director: 5,
  vp: 5,
  ceo: 6,
  founder: 6,
  cto: 6,
  super_admin: 7,
} as const;

/*
// Permission definitions (Hardcoded fallback - Now fully driven from Database)
export const PERMISSIONS: Record<string, Role[]> = {
  // Dashboard
  view_dashboard: [
    "intern",
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],

  // Employee Management
  view_employees: [
    "intern",
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  manage_employees: ["hr_manager", "admin"],
  view_employee_details: [
    "intern",
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],

  // Attendance
  view_attendance: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  manage_attendance: ["team_leader", "project_manager", "manager", "admin"],
  approve_attendance: ["team_leader", "project_manager", "manager", "admin"],

  // Leave Management
  view_leave: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  apply_leave: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  recommend_leave: [
    "team_leader",
    "project_manager",
    "manager",
  ],
  approve_leave: [
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  approve_wfh: [
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  leave_view_only: [], // View all employee leaves without approve/reject/cancel; assign explicitly
  manage_leave_policies: ["admin", "hr_manager"],

  // Holiday Management
  view_holidays: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
  ],
  manage_holidays: ["hr_manager", "admin"],

  // Timesheet Management
  view_timesheet: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  create_timesheet: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  edit_timesheet: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  delete_timesheet: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  submit_timesheet: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  approve_timesheet: ["team_leader", "project_manager", "manager", "admin"],
  reject_timesheet: ["team_leader", "project_manager", "manager", "admin"],

  // Shifts (admin and HR only)
  view_shifts: ["hr_manager", "admin"],
  manage_shifts: ["hr_manager", "admin"],
  assign_shifts: ["hr_manager", "admin"],

  // Analytics & Reports
  view_reports: [
    "project_manager",
    "manager",
    "admin",
    "hr_manager",
    "ceo",
    "director",
    "vp",
    "founder",
    "cto",
  ],
  view_analytics: [
    "project_manager",
    "manager",
    "admin",
    "hr_manager",
    "ceo",
    "director",
    "vp",
    "founder",
    "cto",
  ],
  export_reports: ["admin", "hr_manager", "ceo"],
  view_announcements: [
    "intern",
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  post_announcements: ["admin", "hr_manager"],

  // Settings & System
  view_settings: ["admin"],
  manage_settings: ["admin"],
  manage_roles: ["hr_manager", "admin"],
  manage_permissions: ["admin"],

  // Department Management
  view_departments: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  manage_departments: [
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],

  // Project Management
  view_projects: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],
  manage_projects: ["admin", "manager", "project_manager"],

  // Org Tree (every employee can view full org tree)
  view_org_tree: [
    "intern",
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "hr_manager",
    "admin",
    "director",
    "vp",
    "ceo",
    "founder",
    "cto",
  ],

  // Biometric (HR only)
  view_biometric: ["hr_manager", "admin"],

  // User Profile
  view_profile: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  edit_profile: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],
  change_password: [
    "employee",
    "team_leader",
    "project_manager",
    "manager",
    "admin",
  ],

  // Company Management (Super Admin)
  view_companies: ["super_admin"],
  manage_companies: ["super_admin"],

  // Finance Operation (Finance department only)
  view_finance_operation: [],
  manage_invoices: [],
};
*/

export type Permission = string;
export type Role = keyof typeof ROLE_HIERARCHY;

// Navigation items with role requirements, grouped logically
export const NAV_ITEMS = [
  // --- Main ---
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: "HomeIcon",
    permission: "view_dashboard" as Permission,
    group: "main",
  },
  // --- People & Organization ---
  {
    name: "Employees",
    href: "/dashboard/employees",
    icon: "UsersIcon",
    permission: "view_employees" as Permission,
    group: "people",
  },
  {
    name: "Departments",
    href: "/dashboard/departments",
    icon: "BuildingOfficeIcon",
    permission: "view_departments" as Permission,
    group: "people",
  },
  {
    name: "Org Tree",
    href: "/dashboard/org-tree",
    icon: "Squares2X2Icon",
    permission: "view_org_tree" as Permission,
    group: "people",
  },
  // --- Time & Leave ---
  {
    name: "Leave",
    href: "/dashboard/leave",
    icon: "CalendarDaysIcon",
    permission: "view_leave" as Permission,
    group: "time",
  },
  {
    name: "Holidays",
    href: "/dashboard/holidays",
    icon: "CalendarDaysIcon",
    permission: "view_holidays" as Permission,
    group: "time",
  },
  {
    name: "Leave Credit",
    href: "/dashboard/leave/credit",
    icon: "BanknotesIcon",
    permission: "manage_leave_policies" as Permission,
    group: "time",
  },
  // { name: 'Shifts', href: '/dashboard/shifts', icon: 'ClockIcon', permission: 'view_shifts' as Permission, group: 'time' },
  // --- Work ---
  {
    name: "Projects",
    href: "/dashboard/projects",
    icon: "FolderIcon",
    permission: "view_projects" as Permission,
    group: "work",
  },
  {
    name: "Timesheet",
    href: "/dashboard/timesheet",
    icon: "DocumentTextIcon",
    permission: "view_timesheet" as Permission,
    group: "work",
  },
  // --- Communication ---
  {
    name: "Announcements",
    href: "/dashboard/announcements",
    icon: "MegaphoneIcon",
    permission: "view_announcements" as Permission,
    group: "communication",
  },
  // --- Insights ---
  {
    name: "Analytics",
    href: "/dashboard/analytics",
    icon: "ChartBarIcon",
    permission: "view_analytics" as Permission,
    group: "insights",
  },
  {
    name: "Reports",
    href: "/dashboard/reports",
    icon: "DocumentTextIcon",
    permission: "view_reports" as Permission,
    group: "insights",
  },
  // --- Finance ---
  {
    name: "Finance Operation",
    href: "/dashboard/finance-operation",
    icon: "BanknotesIcon",
    permission: "view_finance_operation" as Permission,
    group: "finance",
  },
  // --- Administration ---
  {
    name: "Settings",
    href: "/dashboard/settings",
    icon: "Cog6ToothIcon",
    permission: "view_settings" as Permission,
    group: "admin",
  },
  {
    name: "User Management",
    href: "/dashboard/admin/users",
    icon: "UsersIcon",
    permission: "manage_employees" as Permission,
    group: "admin",
  },
  {
    name: "Role Management",
    href: "/dashboard/admin/roles",
    icon: "ShieldCheckIcon",
    permission: "manage_roles" as Permission,
    group: "admin",
  },
  {
    name: "Company Management",
    href: "/dashboard/admin/companies",
    icon: "BuildingOfficeIcon",
    permission: "view_companies" as Permission,
    group: "admin",
  },
  // { name: 'Biometric', href: '/dashboard/biometric', icon: 'FingerPrintIcon', permission: 'view_biometric' as Permission, group: 'admin' },
] as const;

interface AuthContextType {
  currentUser: User | null;
  currentRole: Role;
  isAuthenticated: boolean;
  isLoading: boolean;

  // RBAC utilities
  can: (permission: Permission) => boolean;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasAllRoles: (roles: Role[]) => boolean;

  // Role switching (dev only)
  switchRole: (role: Role) => void;
  resetRole: () => void;
  isRoleSwitched: boolean;

  // Navigation utilities
  getVisibleNavItems: () => Array<(typeof NAV_ITEMS)[number]>;
  canAccessRoute: (permission: Permission) => boolean;

  // Auth actions
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<boolean>;
  logout: () => Promise<void>;
  setAuth: (user: User, tokens: AuthTokens) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading, login, logout, setAuth } =
    useAuth();

  // Role switching state (dev only)
  const [switchedRole, setSwitchedRole] = useState<Role | null>(null);
  const [isRoleSwitched, setIsRoleSwitched] = useState(false);
  const previousUserRef = useRef<{ id: string; email: string } | null>(null);

  // Single source of truth: derive currentRole from switchedRole or user
  const currentRole = useMemo<Role>(
    () => switchedRole ?? (user?.role as Role) ?? "employee",
    [switchedRole, user?.role],
  );

  // Effect 1: Reset on mount
  useEffect(() => {
    setSwitchedRole(null);
    setIsRoleSwitched(false);
  }, []);

  // Effect 2: Reset role switching when user changes (login/switch user)
  useEffect(() => {
    if (user?.id && user.id !== previousUserRef.current?.id) {
      setSwitchedRole(null);
      setIsRoleSwitched(false);
      previousUserRef.current = { id: user.id, email: user.email };
    } else if (user?.id && previousUserRef.current === null) {
      previousUserRef.current = { id: user.id, email: user.email };
    }
  }, [user?.id, user?.email]);

  // Effect 3: Reset on logout
  useEffect(() => {
    if (!user) {
      setSwitchedRole(null);
      setIsRoleSwitched(false);
      previousUserRef.current = null;
    }
  }, [user]);

  // Check if user has a specific permission
  const can = useCallback(
    (permission: Permission): boolean => {
      if (!user) {
        return false;
      }

      if (!isAuthenticated) {
        return false;
      }

      // Super admin has supreme access — every permission, everywhere.
      // This ensures the sidebar surfaces every admin screen in addition to the
      // super-admin-only screens (Company Management, etc.).
      if (currentRole === "super_admin") {
        return true;
      }

      // Finance Operation: Finance department OR Admin
      if (permission === "view_finance_operation") {
        if (currentRole === "admin") return true;
        const deptName =
          (user as { departmentDetails?: { name: string } | null })
            .departmentDetails?.name ?? "";
        return deptName.toLowerCase().includes("finance");
      }
      if (permission === "manage_invoices") {
        if (currentRole === "admin") return true;
        const deptName =
          (user as { departmentDetails?: { name: string } | null })
            .departmentDetails?.name ?? "";
        return deptName.toLowerCase().includes("finance");
      }

      // Org Tree: Fall back to view_employees if view_org_tree is not yet seeded
      if (permission === "view_org_tree") {
        if (user.permissions?.includes("view_org_tree")) return true;
        return user.permissions?.includes("view_employees") ?? false;
      }

      // Check database-driven effective permissions
      return user.permissions?.includes(permission) ?? false;
    },
    [user, currentRole, isAuthenticated],
  );

  // Check if user has a specific role
  const hasRole = useCallback(
    (role: Role): boolean => {
      if (!user) return false;
      return currentRole === role;
    },
    [user, currentRole],
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (roles: Role[]): boolean => {
      if (!user) return false;
      return roles.includes(currentRole);
    },
    [user, currentRole],
  );

  // Check if user has all of the specified roles (useful for admin+manager scenarios)
  const hasAllRoles = useCallback(
    (roles: Role[]): boolean => {
      if (!user) return false;
      return roles.every((role) => {
        const userRoleLevel = ROLE_HIERARCHY[currentRole];
        const requiredRoleLevel = ROLE_HIERARCHY[role];
        return userRoleLevel >= requiredRoleLevel;
      });
    },
    [user, currentRole],
  );

  // Switch role (dev only)
  const switchRole = useCallback(
    (role: Role) => {
      if (user?.role === "admin") {
        setSwitchedRole(role);
        setIsRoleSwitched(true);
      } else {
        console.warn("Role switching is only available for admin users");
      }
    },
    [user?.role],
  );

  // Reset role to actual user role
  const resetRole = useCallback(() => {
    setSwitchedRole(null);
    setIsRoleSwitched(false);
  }, []);

  // Get navigation items visible to current user - memoized to prevent unnecessary recalculations
  const getVisibleNavItems = useCallback(() => {
    if (!user || !isAuthenticated) {
      return [];
    }

    const visibleItems = NAV_ITEMS.filter((item) => {
      const hasPermission = can(item.permission);
      return hasPermission;
    });

    return visibleItems;
  }, [user, isAuthenticated, can]);

  // Check if user can access a specific route
  const canAccessRoute = useCallback(
    (permission: Permission): boolean => {
      return can(permission);
    },
    [can],
  );

  const value: AuthContextType = useMemo(
    () => ({
      currentUser: user,
      currentRole,
      isAuthenticated,
      isLoading,
      can,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      switchRole,
      resetRole,
      isRoleSwitched,
      getVisibleNavItems,
      canAccessRoute,
      login,
      logout,
      setAuth,
    }),
    [
      user,
      currentRole,
      isAuthenticated,
      isLoading,
      isRoleSwitched,
      can,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      switchRole,
      resetRole,
      getVisibleNavItems,
      canAccessRoute,
      login,
      logout,
      setAuth,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use AuthContext
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

// Utility function for permission checking (can be used outside components)
export const can = (
  permission: Permission,
  user: User | null,
  currentRole?: Role,
): boolean => {
  if (!user) return false;

  const role = currentRole || user.role;

  // Super admin has supreme access — bypass per-permission role lists.
  if (role === "super_admin") return true;

  // Check if user has permission (populated from DB on login)
  return user.permissions?.includes(permission) ?? false;
};

// Utility function for role checking
export const hasRole = (
  role: Role,
  user: User | null,
  currentRole?: Role,
): boolean => {
  if (!user) return false;
  return (currentRole || user.role) === role;
};

// Utility function for multiple role checking
export const hasAnyRole = (
  roles: Role[],
  user: User | null,
  currentRole?: Role,
): boolean => {
  if (!user) return false;
  return roles.includes(currentRole || user.role);
};
